import React, { useState, useEffect, useMemo } from "react";
import styled, { css } from "styled-components";
import { useCart } from "../context/CartContext";
import swishImage from "../assets/images/swish.png";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faCreditCard,
    faMobile,
    faExclamationTriangle,
    faTimes,
    faStar,
    faWallet,
} from "@fortawesome/free-solid-svg-icons";
import BottomMenu from "../components/BottomMenu";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
    fetchPayeeAlias,
    buildPaymentData,
    initiateSwish,
    makeStatusQueryOptions,
    openSwishUrlCompat,
} from "../services/swishService";
import { API_BASE } from "../queries/swish";
import { sendQrNotOpenTicket } from "../services/tickets";
import { getSwishPaymentStatus } from "../queries/swish";
import { startQrOpenWatchdog, cancelQrOpenWatchdog } from "../services/utils/qrWatchdog";
import { getVoucherBalance, handleVoucherBalancePay } from "../services/vouchersHandling";

export default function ChoosePayment() {
    const { cartItems, setCartItems } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, token } = useAuth();

    const voucherMode = !!(location.state && location.state.voucherMode);

    const [error, setError] = useState(null);
    const [isSwishProcessing, setIsSwishProcessing] = useState(false);
    const [paymentRequestId, setPaymentRequestId] = useState(null);
    const [voucherBalance, setVoucherBalance] = useState(null);
    const [isVoucherProcessing, setIsVoucherProcessing] = useState(false);

    // Fetch user's voucher balance for enabling the balance method
    useEffect(() => {
        let cancelled = false;
        async function run() {
            try {
                if (!user?.id || !token) {
                    if (!cancelled) setVoucherBalance(null);
                    return;
                }
                const bal = await getVoucherBalance({ userId: user.id, token });
                if (!cancelled) setVoucherBalance(bal);
            } catch {
                if (!cancelled) setVoucherBalance(null);
            }
        }
        run();
        return () => { cancelled = true; };
    }, [user, token]);

    // Helper: fetch status with small retries if qrCode missing
    async function fetchQrStatusWithRetry(pid, tries = 2, delayMs = 500) {
        if (!pid) return { qrCode: null, qrValidUntil: null };
        let last = null;
        for (let i = 0; i < tries; i++) {
            try {
                const resp = await getSwishPaymentStatus(pid);
                const qrCode = resp?.qrCode ?? resp?.data?.qrCode ?? null;
                const qrValidUntil = resp?.qrValidUntil ?? resp?.data?.qrValidUntil ?? null;
                if (qrCode) return { qrCode, qrValidUntil };
                last = { qrCode, qrValidUntil };
            } catch { /* ignore */ }
            if (i < tries - 1) await new Promise(r => setTimeout(r, delayMs));
        }
        return last || { qrCode: null, qrValidUntil: null };
    }

    useEffect(() => {
        try {
            localStorage.removeItem("paymentStatus");
            localStorage.removeItem("paymentConfirmed");
        } catch {}
    }, []);

    useEffect(() => {
        if (voucherMode) return; // Skip QR storage handler in voucher mode
        const onStorage = async (e) => {
            if (e.key !== "paymentStatus") return;
            const val = String(e.newValue || "").toUpperCase();
            if (val === "PAID") {
                const pid = localStorage.getItem("lastPaymentId") || paymentRequestId;
                let { qrCode, qrValidUntil } = await fetchQrStatusWithRetry(pid, 2, 500);
                navigate(`/qrcode?paymentId=${encodeURIComponent(pid || "")}`, { replace: true, state: { qrCode, qrValidUntil } });
            } else if (["CANCELLED","DECLINED","ERROR"].includes(val)) {
                try { cancelQrOpenWatchdog("storage_terminal"); } catch {}
                navigate("/products", { replace: true });
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [navigate, paymentRequestId, voucherMode]);


    const totalAmount = useMemo(
        () => cartItems.reduce((acc, item) => acc + Number(item.price) * Number(item.quantity), 0),
        [cartItems]
    );

    const canPayWithBalance = Number.isFinite(Number(voucherBalance)) && Number(voucherBalance) >= Number(totalAmount);

    const initiateSwishMutation = useMutation({
        mutationFn: async () => {
            const fridgeIdFromLs = Number(localStorage.getItem("selectedFridgeId") || 0);
            if (!fridgeIdFromLs) {
                throw new Error("Ingen enhet vald");
            }
            const alias = await fetchPayeeAlias(token);
            const paymentData = buildPaymentData({
                alias,
                userId: user.id,
                cartItems,
                amount: totalAmount,
                fridgeId: fridgeIdFromLs,
            });
            return await initiateSwish({ token, paymentData, fridgeId: fridgeIdFromLs });
        },
        onMutate: () => {
            setIsSwishProcessing(true);
            setError(null);
            try {
                localStorage.removeItem("paymentStatus");
                // historySaved flag removed after migration to PaymentRecords
                cancelQrOpenWatchdog("new_initiate");
            } catch {}
        },
        onError: (err) => {
            setError(err?.message === "Ingen enhet vald" ? "Välj en enhet innan betalning." : "Swish-förfrågan misslyckades. Försök igen.");
            setIsSwishProcessing(false);
        },
        onSuccess: ({ paymentRequestId, swishUrl }) => {
            setPaymentRequestId(paymentRequestId);
            try {
                localStorage.setItem("lastPaymentId", paymentRequestId);
                // Removed early watchdog start; we only arm after confirmed PAID status
                cancelQrOpenWatchdog("new_initiate_rearm");
            } catch {}
            setTimeout(() => { try { openSwishUrlCompat(swishUrl); } catch {} }, 150);
            setIsSwishProcessing(false);
        },
    });

    const { isFetching: isPolling } = useQuery(
        makeStatusQueryOptions(paymentRequestId, {
            onError: () => {},
            onTerminal: (status) => {
                try { localStorage.setItem("paymentStatus", String(status || "").toUpperCase()); } catch {}
                try { cancelQrOpenWatchdog("poll_terminal"); } catch {}
                setError("Betalningen misslyckades. Försök igen.");
                const raw = localStorage.getItem("isVoucherFridge");
                let goVoucher = false;
                try { goVoucher = JSON.parse(raw ?? "false"); } catch { goVoucher = raw === "true"; }
                const target = goVoucher || voucherMode ? "/cart-voucher" : "/products";
                const selectedFridgeId = localStorage.getItem("selectedFridgeId") ?? null;
                navigate(target, { replace: true, state: { selectedFridgeId } });
            },
            onPaid: async ({ qrCode, qrValidUntil }) => {
                if (voucherMode) {
                    // Voucher mode: server credits on callback. Just clear cart, flag success, and go back to voucher cart.
                    try {
                        localStorage.setItem("paymentStatus", "PAID");
                        if (paymentRequestId) localStorage.setItem("lastPaymentId", paymentRequestId);
                        localStorage.setItem("voucherTopupSuccess", "true");
                    } catch {}
                    setCartItems([]);
                    const selectedFridgeId = localStorage.getItem("selectedFridgeId") ?? null;
                    navigate("/cart-voucher", { replace: true, state: { selectedFridgeId } });
                    return;
                }
                // Normal flow: QR
                try {
                    const fridgeIdFromLs = Number(localStorage.getItem("selectedFridgeId") || 0);
                    startQrOpenWatchdog({
                        paymentRequestId,
                        fridgeId: fridgeIdFromLs,
                        amount: totalAmount,
                        cartItems,
                        user,
                        phase: 'paid',
                        visibleGraceMs: 12000,
                        freshdeskSender: (details) => sendQrNotOpenTicket({ details, apiBase: API_BASE, token })
                    });
                } catch (e) { console.error('[ChoosePayment] watchdog arm error', e); }
                // If qrCode missing, attempt one immediate retry fetch
                if (!qrCode) {
                    const pid = paymentRequestId;
                    const retry = await fetchQrStatusWithRetry(pid, 2, 400);
                    qrCode = retry.qrCode; // local var update
                    qrValidUntil = retry.qrValidUntil;
                }
                try {
                    localStorage.setItem("paymentStatus", "PAID");
                    if (paymentRequestId) localStorage.setItem("lastPaymentId", paymentRequestId);
                } catch {}
                navigate(`/qrcode?paymentId=${encodeURIComponent(paymentRequestId || "")}`, { replace: true, state: { qrCode, qrValidUntil } });
            }
        })
    );

    // Disable if fridge not selected
    const fridgeIdCurrent = Number(localStorage.getItem("selectedFridgeId") || 0);
    const paymentDisabled = !fridgeIdCurrent;

    const isBusyAny = isSwishProcessing || isPolling || isVoucherProcessing;

    return (
        <Container>
            <ContentWrapper>
                <Header>
                    <BackButton onClick={() => {
                        const raw = localStorage.getItem("isVoucherFridge");
                        let goVoucher = false;
                        try { goVoucher = JSON.parse(raw ?? "false"); } catch { goVoucher = raw === "true"; }
                        const target = goVoucher || voucherMode ? "/cart-voucher" : "/cart";
                        const selectedFridgeId = localStorage.getItem("selectedFridgeId") ?? null;
                        navigate(target, { state: { selectedFridgeId } });
                    }}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </BackButton>
                    <HeaderTitle>Betalsätt</HeaderTitle>
                    <PlaceholderDiv />
                </Header>

                <TotalSummary>
                    <TotalLabel>Totalt att betala</TotalLabel>
                    <TotalAmount>{totalAmount} kr</TotalAmount>
                </TotalSummary>

                {error && (
                    <ErrorAlert>
                        <ErrorIcon>
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                        </ErrorIcon>
                        <ErrorContent>
                            <ErrorTitle>Betalning misslyckades</ErrorTitle>
                            <ErrorMessage>{error}</ErrorMessage>
                        </ErrorContent>
                        <CloseErrorButton onClick={() => setError(null)}>
                            <FontAwesomeIcon icon={faTimes} />
                        </CloseErrorButton>
                    </ErrorAlert>
                )}

                <SectionTitle>Välj betalsätt</SectionTitle>

                <PaymentCard
                    $busy={isBusyAny}
                    onClick={() => {
                        if (isBusyAny || paymentDisabled) return;
                        initiateSwishMutation.mutate();
                    }}
                    aria-busy={isSwishProcessing || (isPolling && !!paymentRequestId)}
                    aria-live="polite"
                >
                    <RecommendedBadge>
                        <FontAwesomeIcon icon={faStar} />
                        Rekommenderad
                    </RecommendedBadge>

                    <PaymentIconContainer $pulsing={isSwishProcessing || (isPolling && !!paymentRequestId)}>
                        <PaymentIconImageWhite src={swishImage} alt="Swish" />
                    </PaymentIconContainer>

                    <PaymentInfo>
                        <PaymentName>
                            Swish
                        </PaymentName>
                        <PaymentSubtitle>
                            Snabb & säker betalning
                        </PaymentSubtitle>
                    </PaymentInfo>

                    {(isSwishProcessing || (isPolling && !!paymentRequestId)) && (
                        <BusyOverlay role="status" aria-label="Initierar Swish">
                            <Spinner />
                            <BusyText>Initierar...</BusyText>
                        </BusyOverlay>
                    )}
                </PaymentCard>

                {/* Betala med saldo: enabled only when saldo >= totalt belopp. Triggers voucher purchase. */}
                <PaymentCard
                    style={(!canPayWithBalance || isBusyAny || paymentDisabled) ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                    onClick={async () => {
                        if (!canPayWithBalance || isBusyAny || paymentDisabled) return;
                        const fridgeIdFromLs = Number(localStorage.getItem("selectedFridgeId") || 0);
                        if (!fridgeIdFromLs) { setError("Välj en enhet innan betalning."); return; }
                        try {
                            setIsVoucherProcessing(true);
                            setError(null);
                            const res = await handleVoucherBalancePay({ selectedFridgeId: fridgeIdFromLs, cartItems, token, voucherBalance });
                            setIsVoucherProcessing(false);
                            if (!res?.ok) {
                                setError(res?.error || "Köpet misslyckades.");
                                return;
                            }
                            try { setCartItems([]); } catch {}
                            const code = res?.unlockCode || res?.result?.qrCode || res?.result?.unlockCode || "";
                            if (code) {
                                navigate(`/qrcode`, { replace: true, state: { qrCode: code } });
                            } else {
                                navigate("/products", { replace: true });
                            }
                        } catch (_) {
                            setIsVoucherProcessing(false);
                            setError("Något gick fel vid saldo-betalning. Försök igen.");
                        }
                    }}
                >
                    <PaymentIconContainer>
                        <FontAwesomeIcon color="#fff" icon={faWallet} />
                    </PaymentIconContainer>
                    <PaymentInfo>
                        <PaymentName>Betala med saldo</PaymentName>
                        <PaymentSubtitle>
                            {voucherBalance === null
                                ? "Hämtar saldo..."
                                : (canPayWithBalance
                                    ? "Betala direkt med ditt saldo"
                                    : "Ditt saldo är lägre än totalsumman")}
                        </PaymentSubtitle>
                    </PaymentInfo>
                </PaymentCard>

                <PaymentCard style={{ opacity: 0.5, cursor: "not-allowed" }}>
                    <PaymentIconContainer>
                        <FontAwesomeIcon color="#fff" icon={faCreditCard} />
                    </PaymentIconContainer>
                    <PaymentInfo>
                        <PaymentName>Betalkort</PaymentName>
                        <PaymentSubtitle>Visa, Mastercard, Amex</PaymentSubtitle>
                    </PaymentInfo>
                </PaymentCard>

                <PaymentCard style={{ opacity: 0.5, cursor: "not-allowed" }}>
                    <PaymentIconContainer>
                        <FontAwesomeIcon color="#fff" icon={faMobile} />
                    </PaymentIconContainer>
                    <PaymentInfo>
                        <PaymentName>Apple Pay</PaymentName>
                        <PaymentSubtitle>Touch ID eller Face ID</PaymentSubtitle>
                    </PaymentInfo>
                </PaymentCard>
            </ContentWrapper>

            <BottomMenuSpacer />
            <BottomMenu />
        </Container>
    );
}


const Container = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #f6f7fa 0%, #e9ecef 100%);
    position: relative;
`;
const ContentWrapper = styled.div`
    padding: 20px;
    max-width: 600px;
    margin: 0 auto;
`;
const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 32px;
    padding-top: 20px;
`;
const BackButton = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.8);
    color: #374151;
    display: flex;
    align-items: center;
    justify-content: center;
`;
const HeaderTitle = styled.h1`
    font-size: 24px;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
`;
const PlaceholderDiv = styled.div`
    width: 44px;
`;

const TotalSummary = styled.div`
    background: linear-gradient(135deg, rgba(37, 37, 37, 0.95) 0%, rgba(55, 65, 81, 0.95) 100%);
    border-radius: 20px;
    padding: 28px 24px;
    text-align: center;
    margin-bottom: 32px;
    color: white;
`;
const TotalLabel = styled.div`
    font-size: 16px;
    opacity: 0.8;
    margin-bottom: 8px;
    font-weight: 500;
`;
const TotalAmount = styled.div`
    font-size: 36px;
    font-weight: 700;
    margin-bottom: 16px;
    background: linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const ErrorAlert = styled.div`
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.2);
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 24px;
    display: flex;
    gap: 16px;
`;
const ErrorIcon = styled.div`
    width: 24px;
    height: 24px;
    color: #ef4444;
`;
const ErrorContent = styled.div`
    flex: 1;
`;
const ErrorTitle = styled.div`
    font-weight: 600;
    color: #991b1b;
    margin-bottom: 4px;
`;
const ErrorMessage = styled.div`
    color: #dc2626;
    font-size: 14px;
`;
const CloseErrorButton = styled.button`
    width: 24px;
    height: 24px;
    border: none;
    background: none;
    color: #ef4444;
`;

const SectionTitle = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 20px 0;
`;

const PaymentCard = styled.div`
    background: rgba(255, 255, 255, 0.9);
    border: 2px solid rgba(255, 255, 255, 0.8);
    border-radius: 20px;
    padding: 24px;
    margin-bottom: 16px;
    position: relative;
    display: flex;
    align-items: center;
    gap: 20px;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.3s ease, background 0.3s ease;

    &:active {
        transform: scale(0.97);
    }

    &:hover {
        box-shadow: 0 8px 24px rgba(0,0,0,0.08);
    }

    ${({ $busy }) => $busy && css`
        cursor: default;
        pointer-events: none; /* still block clicks, but allow overlay animation */
        opacity: 0.95;
    `}
`;

const RecommendedBadge = styled.div`
    position: absolute;
    top: -1px;
    right: 20px;
    background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
    color: white;
    padding: 6px 12px;
    border-radius: 0 0 12px 12px;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const PaymentIconContainer = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 16px;
    background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
    ${({ $pulsing }) => $pulsing && css`
        animation: pulseScale 1.2s ease-in-out infinite alternate;
        @keyframes pulseScale {
            0% { transform: scale(1); }
            100% { transform: scale(1.08); }
        }
    `}
`;

const PaymentIconImageWhite = styled.img`
    width: 48px;
    height: 32px;
    object-fit: contain;
    filter: brightness(0) invert(1);
`;

const PaymentInfo = styled.div`
    flex: 1;
`;
const PaymentName = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 4px;
`;
const PaymentSubtitle = styled.div`
    font-size: 14px;
    color: #6b7280;
`;

const BottomMenuSpacer = styled.div`
    height: 80px;
`;

const BusyOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(255,255,255,0.78);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    border-radius: inherit;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    animation: fadeInBusy 0.25s ease;
    z-index: 5;
    @keyframes fadeInBusy { from { opacity: 0; } to { opacity: 1; } }
`;

const Spinner = styled.div`
    width: 34px;
    height: 34px;
    border: 3px solid #d1d5db;
    border-top-color: #22c55e;
    border-radius: 50%;
    animation: spinBusy 0.8s linear infinite;
    @keyframes spinBusy { to { transform: rotate(360deg); } }
`;

const BusyText = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #1f2937;
    letter-spacing: 0.5px;
`;

